import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: ${theme.spacing[4]};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid ${theme.colors.light.border.light};
`;

const StyledTableHeader = styled.thead`
  background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
  border-bottom: 2px solid ${theme.colors.primary[200]};
`;

const StyledTableRow = styled.tr<{ hover?: boolean }>`
  border-bottom: 1px solid ${theme.colors.light.border.light};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  ${({ hover = true }) =>
    hover &&
    `
    &:hover {
      background-color: ${theme.colors.primary[50]};
      box-shadow: 0 2px 8px -2px rgba(124, 58, 237, 0.1);
      transform: translateY(-1px);
    }
  `}

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: transparent;
    transition: background 0.2s ease;
  }

  ${({ hover = true }) =>
    hover &&
    `
    &:hover::before {
      background: linear-gradient(180deg, ${theme.colors.primary[500]}, ${theme.colors.primary[600]});
    }
  `}
`;

const StyledTableHeaderCell = styled.th`
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  text-align: left;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[700]};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  position: relative;
  white-space: nowrap;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 25%;
    height: 50%;
    width: 1px;
    background: linear-gradient(180deg, transparent, ${theme.colors.primary[300]}, transparent);
  }
`;

const StyledTableCell = styled.td`
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  vertical-align: middle;
  font-weight: ${theme.typography.fontWeight.normal};
  line-height: 1.5;
`;

const StyledTableBody = styled.tbody`
  ${StyledTableRow} {
    &:nth-child(even) {
      background-color: ${theme.colors.white};
    }

    &:nth-child(odd) {
      background-color: ${theme.colors.light.background.secondary};
    }
  }
`;

const StyledTableFooter = styled.tfoot`
  background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
  border-top: 2px solid ${theme.colors.primary[200]};
`;

const StyledTableFooterCell = styled.td`
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  text-align: center;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.primary[700]};
`;

interface TableProps {
  children: React.ReactNode;
  className?: string;
}

interface TableSubComponents {
  Header: React.FC<{ children: React.ReactNode }>;
  Body: React.FC<{ children: React.ReactNode }>;
  Footer: React.FC<{ children: React.ReactNode }>;
  Row: React.FC<{
    children: React.ReactNode;
    hover?: boolean;
    style?: React.CSSProperties;
    onClick?: () => void;
    id?: string;
  }>;
  HeaderCell: React.FC<{ children: React.ReactNode; style?: React.CSSProperties; colSpan?: number }>;
  FooterCell: React.FC<{ children: React.ReactNode; colSpan?: number }>;
  Cell: React.FC<{ children: React.ReactNode; style?: React.CSSProperties; colSpan?: number }>;
}

export const Table: React.FC<TableProps> & TableSubComponents = ({ children, className }) => {
  return <StyledTable className={className}>{children}</StyledTable>;
};

Table.Header = ({ children }) => <StyledTableHeader>{children}</StyledTableHeader>;
Table.Body = ({ children }) => <StyledTableBody>{children}</StyledTableBody>;
Table.Footer = ({ children }) => <StyledTableFooter>{children}</StyledTableFooter>;
Table.Row = ({ children, hover, style, onClick, id }) => (
  <StyledTableRow id={id} hover={hover} style={style} onClick={onClick}>
    {children}
  </StyledTableRow>
);
Table.HeaderCell = ({ children, style, colSpan }) => (
  <StyledTableHeaderCell style={style} colSpan={colSpan}>
    {children}
  </StyledTableHeaderCell>
);
Table.FooterCell = ({ children, colSpan }) => (
  <StyledTableFooterCell colSpan={colSpan}>{children}</StyledTableFooterCell>
);
Table.Cell = ({ children, style, colSpan }) => (
  <StyledTableCell style={style} colSpan={colSpan}>
    {children}
  </StyledTableCell>
);

